import { FunctionComponent, useState } from 'react';
import { Box } from '@material-ui/core';
import { useCommonStyles } from '../CommonStyles';
import Card from '../UI/Card';
import { cards, CardData, CardTitles } from '../Data/CardData';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useHasPremiumPlan } from 'hooks/premiumPlan';
import NotAvailable from '../NotAvailable/NotAvailable';
import SearchInput from '../components/SearchInput';
import BookingsSummary from './BookingsSummary';
import ResultsNotFound from '../components/ResultsNotFound';
import AppointmentsDetail from './AppointmentsDetail';
import BookingsInsights from './BookingsInsights';

interface Props {
    title: string;
    category: string;
}

const Bookings: FunctionComponent<Props> = ({ title, category }) => {
    const commonClasses = useCommonStyles();
    const hasPremiumPlan = useHasPremiumPlan();

    const [selectedCard, setSelectedCard] = useState<CardData | null>(null);
    const [searchText, setSearchText] = useState('');
    const [showSearch, setShowSearch] = useState(true);

    const handleCardClick = (card: CardData) => {
        setSelectedCard(card);
        setShowSearch(false);
    };

    const showDefaultView = () => {
        setSelectedCard(null);
        setShowSearch(true);
    };

    const filteredCards = cards.filter(
        card =>
            card.category === category &&
            card.active &&
            (card.title.toLowerCase().includes(searchText.toLowerCase()) ||
                card.description.toLowerCase().includes(searchText.toLowerCase()))
    );

    const handleClickSearch = (): void => {
        setSearchText('');
    };

    const renderContent = () => {
        if (!selectedCard) {
            return (
                <Box
                    display="flex"
                    flexWrap="wrap"
                    className={commonClasses.cardsContent}
                >
                    {filteredCards
                        .filter(card => card.category === category)
                        .map((card, index) => (
                            <Card
                                key={index}
                                title={card.title}
                                subTitle={card.category}
                                description={card.description}
                                premium={card.premium}
                                icon={card.icon}
                                onClick={() => {
                                    handleCardClick(card);
                                }}
                            />
                        ))}
                </Box>
            );
        }

        // Return different components based on selected Card
        switch (selectedCard.title) {
            case CardTitles.BOOKINGS_SUMMARY:
                return selectedCard.premium && !hasPremiumPlan ? (
                    <NotAvailable />
                ) : (
                    <BookingsSummary description={selectedCard.description} />
                );
            case CardTitles.BOOKINGS_INSIGHTS:
                return selectedCard.premium && !hasPremiumPlan ? (
                    <NotAvailable />
                ) : (
                    <BookingsInsights description={selectedCard.description} />
                );
            case CardTitles.APPOINTMENTS_DETAIL:
                return selectedCard.premium && !hasPremiumPlan ? (
                    <NotAvailable />
                ) : (
                    <AppointmentsDetail description={selectedCard.description} />
                );
            case CardTitles.WEEKLY_BREAKDOWN:
                return selectedCard.premium && !hasPremiumPlan ? (
                    <NotAvailable />
                ) : (
                    <div>Weekly Breakdown Component</div>
                );
            case CardTitles.MONTHLY_BREAKDOWN:
                return selectedCard.premium && !hasPremiumPlan ? (
                    <NotAvailable />
                ) : (
                    <div>Monthly Breakdown Component</div>
                );
            case CardTitles.CANCELLATION_NO_SHOWS:
                return selectedCard.premium && !hasPremiumPlan ? (
                    <NotAvailable />
                ) : (
                    <div>Cancellation & No Shows Component</div>
                );
            case CardTitles.ACTIVITY_LOG:
                return selectedCard.premium && !hasPremiumPlan ? <NotAvailable /> : <div>Activity Log Component</div>;
            default:
                return null;
        }
    };

    const bookingsNotFound = () => {
        return (
            <div className={commonClasses.resultNotFound}>
                <ResultsNotFound title="No reports found" />
            </div>
        );
    };

    return (
        <div className={commonClasses.container}>
            <div className={commonClasses.contentInsights}>
                <div className={commonClasses.subViewTitle}>
                    {selectedCard && (
                        <FontAwesomeIcon
                            className={commonClasses.backIcon}
                            icon={faArrowLeft}
                            onClick={showDefaultView}
                        />
                    )}
                    <h2 className={commonClasses.titleDefault}>{selectedCard ? selectedCard.title : title}</h2>
                </div>
                {showSearch && (
                    <SearchInput
                        searchText={searchText}
                        setSearchText={setSearchText}
                        showSearch={showSearch}
                        handleClickSearch={handleClickSearch}
                    />
                )}
                {filteredCards.length === 0 && bookingsNotFound()}
                {renderContent()}
            </div>
        </div>
    );
};

export default Bookings;
